import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/config';
import HealthDetailDrawer from './HealthDetailDrawer';

// Time preset options
const TIME_PRESETS = [
  { label: '15m', value: 15 * 60 * 1000 },
  { label: '1h', value: 60 * 60 * 1000 },
  { label: '6h', value: 6 * 60 * 60 * 1000 },
  { label: '24h', value: 24 * 60 * 60 * 1000 },
  { label: '7d', value: 7 * 24 * 60 * 60 * 1000 },
];

// Status colors
const STATUS_COLORS = {
  ok: { bg: '#1b5e20', text: '#fff', label: 'OK' },
  attention: { bg: '#f57c00', text: '#fff', label: 'Attention' },
  down: { bg: '#c62828', text: '#fff', label: 'Down' },
};

// Blank reason colors (v1.2.5+)
const BLANK_REASON_COLORS = {
  NONE: { bg: '#1b5e20', text: '#fff', label: 'Healthy' },
  EMPTY: { bg: '#616161', text: '#fff', label: 'No Flights' },
  FILTERED_OUT: { bg: '#f57c00', text: '#fff', label: 'Filtered' },
  BROKEN_UI: { bg: '#c62828', text: '#fff', label: 'Broken UI' },
  NETWORK_STALE: { bg: '#fbc02d', text: '#000', label: 'Stale' },
  WEBVIEW_FREEZE: { bg: '#7b1fa2', text: '#fff', label: 'Frozen' },
};

// Format relative time (e.g., "2 min ago")
function formatRelativeTime(dateStr) {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return 'Invalid';

  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  return `${diffDay}d ago`;
}

// Format ISO date for display
function formatDateTime(dateStr) {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return 'Invalid';
  return date.toLocaleString();
}

export default function HealthDashboard() {
  // State
  const [devices, setDevices] = useState([]);
  const [summary, setSummary] = useState({ total: 0, ok: 0, attention: 0, down: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [timePreset, setTimePreset] = useState('1h');
  const [customRange, setCustomRange] = useState({ from: '', to: '' });
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [appTypeFilter, setAppTypeFilter] = useState('all');
  const [versionFilter, setVersionFilter] = useState('all');
  const [availableVersions, setAvailableVersions] = useState([]);

  // Detail drawer
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Calculate time range based on preset or custom
  const getTimeRange = useCallback(() => {
    if (useCustomRange && customRange.from && customRange.to) {
      return {
        from: new Date(customRange.from).toISOString(),
        to: new Date(customRange.to).toISOString(),
      };
    }
    const preset = TIME_PRESETS.find(p => p.label === timePreset);
    const ms = preset ? preset.value : 60 * 60 * 1000;
    const now = new Date();
    return {
      from: new Date(now - ms).toISOString(),
      to: now.toISOString(),
    };
  }, [timePreset, customRange, useCustomRange]);

  // Fetch versions for filter dropdown
  const fetchVersions = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/heartbeats/versions`);
      if (response.data?.success) {
        setAvailableVersions(response.data.versions || []);
      }
    } catch (err) {
      console.error('Failed to fetch versions:', err);
    }
  };

  // Fetch health data
  const fetchHealthData = useCallback(async () => {
    try {
      setError(null);
      const { from, to } = getTimeRange();

      const params = { from, to };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (appTypeFilter !== 'all') params.appType = appTypeFilter;
      if (versionFilter !== 'all') params.version = versionFilter;
      if (searchQuery.trim()) params.search = searchQuery.trim();

      const response = await axios.get(`${API_BASE_URL}/api/admin/heartbeats/health`, { params });

      if (response.data?.success) {
        setDevices(response.data.devices || []);
        setSummary(response.data.summary || { total: 0, ok: 0, attention: 0, down: 0 });
      } else {
        throw new Error(response.data?.message || 'Unknown error');
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching health data:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch health data');
      setDevices([]);
      setSummary({ total: 0, ok: 0, attention: 0, down: 0 });
      setLoading(false);
    }
  }, [getTimeRange, statusFilter, appTypeFilter, versionFilter, searchQuery]);

  // Initial load
  useEffect(() => {
    fetchVersions();
    fetchHealthData();
  }, []);

  // Handle device row click
  const handleDeviceClick = (device) => {
    setSelectedDevice(device);
    setDrawerOpen(true);
  };

  // Handle drawer close
  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedDevice(null);
  };

  // Render status chip
  const renderStatusChip = (status) => {
    const config = STATUS_COLORS[status] || STATUS_COLORS.ok;
    return (
      <span style={{
        display: 'inline-block',
        padding: '4px 12px',
        borderRadius: '12px',
        backgroundColor: config.bg,
        color: config.text,
        fontSize: '12px',
        fontWeight: 'bold',
        textTransform: 'uppercase',
      }}>
        {config.label}
      </span>
    );
  };

  // Render app type chip
  const renderAppTypeChip = (appType) => {
    const colors = {
      arrivals: { bg: '#0277bd', text: '#fff' },
      departures: { bg: '#7b1fa2', text: '#fff' },
      unknown: { bg: '#616161', text: '#fff' },
    };
    const config = colors[appType] || colors.unknown;
    return (
      <span style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '8px',
        backgroundColor: config.bg,
        color: config.text,
        fontSize: '11px',
        fontWeight: 'bold',
      }}>
        {appType}
      </span>
    );
  };

  // Render blank reason chip (v1.2.5+)
  const renderBlankReasonChip = (blankReason) => {
    if (!blankReason || blankReason === 'NONE') return null;
    const config = BLANK_REASON_COLORS[blankReason] || BLANK_REASON_COLORS.NONE;
    return (
      <span style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '8px',
        backgroundColor: config.bg,
        color: config.text,
        fontSize: '10px',
        fontWeight: 'bold',
      }}>
        {config.label}
      </span>
    );
  };

  // Render flights ratio (received/displayed)
  const renderFlightsRatio = (received, displayed, domRows) => {
    const hasFilterIssue = received > 0 && displayed === 0;
    const hasRenderIssue = displayed > 0 && domRows === 0;

    return (
      <span style={{
        color: hasFilterIssue ? '#ff9800' : hasRenderIssue ? '#f44336' : '#fff',
        fontWeight: (hasFilterIssue || hasRenderIssue) ? 'bold' : 'normal',
      }}>
        {displayed}/{received}
        {hasFilterIssue && <span title="All flights filtered out"> ⚠</span>}
        {hasRenderIssue && <span title="Render issue - DOM empty"> ❌</span>}
      </span>
    );
  };

  // Styles
  const styles = {
    container: {
      backgroundColor: '#000',
      color: '#fff',
      minHeight: '100%',
      padding: '20px',
    },
    header: {
      marginBottom: '20px',
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      marginBottom: '8px',
    },
    subtitle: {
      fontSize: '14px',
      color: '#999',
    },
    filtersRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '12px',
      marginBottom: '20px',
      alignItems: 'center',
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    },
    filterLabel: {
      fontSize: '11px',
      color: '#888',
      textTransform: 'uppercase',
    },
    select: {
      backgroundColor: '#1a1a1a',
      color: '#fff',
      border: '1px solid #333',
      borderRadius: '4px',
      padding: '8px 12px',
      fontSize: '14px',
      minWidth: '120px',
    },
    input: {
      backgroundColor: '#1a1a1a',
      color: '#fff',
      border: '1px solid #333',
      borderRadius: '4px',
      padding: '8px 12px',
      fontSize: '14px',
    },
    button: {
      backgroundColor: '#1976d2',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      padding: '10px 20px',
      fontSize: '14px',
      cursor: 'pointer',
      fontWeight: 'bold',
    },
    buttonSecondary: {
      backgroundColor: '#333',
      color: '#fff',
      border: '1px solid #555',
      borderRadius: '4px',
      padding: '10px 20px',
      fontSize: '14px',
      cursor: 'pointer',
    },
    summaryCards: {
      display: 'flex',
      gap: '16px',
      marginBottom: '20px',
      flexWrap: 'wrap',
    },
    summaryCard: {
      flex: '1 1 150px',
      backgroundColor: '#1a1a1a',
      borderRadius: '8px',
      padding: '16px',
      textAlign: 'center',
      minWidth: '120px',
    },
    summaryNumber: {
      fontSize: '32px',
      fontWeight: 'bold',
      marginBottom: '4px',
    },
    summaryLabel: {
      fontSize: '12px',
      color: '#888',
      textTransform: 'uppercase',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '14px',
    },
    th: {
      textAlign: 'left',
      padding: '12px 8px',
      borderBottom: '2px solid #333',
      color: '#888',
      fontSize: '12px',
      textTransform: 'uppercase',
      fontWeight: 'normal',
    },
    td: {
      padding: '12px 8px',
      borderBottom: '1px solid #222',
      color: '#fff',
      fontWeight: 'bold',
    },
    row: {
      cursor: 'pointer',
      transition: 'background-color 0.15s',
    },
    rowHover: {
      backgroundColor: '#1a1a1a',
    },
    noData: {
      textAlign: 'center',
      padding: '40px',
      color: '#666',
    },
    loading: {
      textAlign: 'center',
      padding: '40px',
      color: '#888',
    },
    error: {
      backgroundColor: '#4a1c1c',
      border: '1px solid #c62828',
      borderRadius: '4px',
      padding: '16px',
      marginBottom: '20px',
      color: '#ffcdd2',
    },
    tooltip: {
      position: 'relative',
      display: 'inline-block',
    },
    warningValue: {
      color: '#ff9800',
      fontWeight: 'bold',
    },
    errorValue: {
      color: '#f44336',
      fontWeight: 'bold',
    },
    monospace: {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#888',
    },
    presetButton: {
      padding: '6px 12px',
      border: '1px solid #444',
      backgroundColor: '#1a1a1a',
      color: '#fff',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '13px',
    },
    presetButtonActive: {
      padding: '6px 12px',
      border: '1px solid #1976d2',
      backgroundColor: '#1976d2',
      color: '#fff',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: 'bold',
    },
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.title}>Screen Health Dashboard</div>
        <div style={styles.subtitle}>
          Monitor heartbeat status and stability metrics across all display screens
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div style={styles.error}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Filters Row */}
      <div style={styles.filtersRow}>
        {/* Time Presets */}
        <div style={styles.filterGroup}>
          <span style={styles.filterLabel}>Time Range</span>
          <div style={{ display: 'flex', gap: '4px' }}>
            {TIME_PRESETS.map(preset => (
              <button
                key={preset.label}
                onClick={() => {
                  setTimePreset(preset.label);
                  setUseCustomRange(false);
                }}
                style={timePreset === preset.label && !useCustomRange
                  ? styles.presetButtonActive
                  : styles.presetButton}
              >
                {preset.label}
              </button>
            ))}
            <button
              onClick={() => setUseCustomRange(!useCustomRange)}
              style={useCustomRange ? styles.presetButtonActive : styles.presetButton}
            >
              Custom
            </button>
          </div>
        </div>

        {/* Custom Range */}
        {useCustomRange && (
          <>
            <div style={styles.filterGroup}>
              <span style={styles.filterLabel}>From</span>
              <input
                type="datetime-local"
                value={customRange.from}
                onChange={(e) => setCustomRange({ ...customRange, from: e.target.value })}
                style={styles.input}
              />
            </div>
            <div style={styles.filterGroup}>
              <span style={styles.filterLabel}>To</span>
              <input
                type="datetime-local"
                value={customRange.to}
                onChange={(e) => setCustomRange({ ...customRange, to: e.target.value })}
                style={styles.input}
              />
            </div>
          </>
        )}

        {/* Search */}
        <div style={styles.filterGroup}>
          <span style={styles.filterLabel}>Search</span>
          <input
            type="text"
            placeholder="Screen name, IP or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ ...styles.input, minWidth: '180px' }}
          />
        </div>

        {/* Status Filter */}
        <div style={styles.filterGroup}>
          <span style={styles.filterLabel}>Status</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={styles.select}
          >
            <option value="all">All</option>
            <option value="ok">OK</option>
            <option value="attention">Attention</option>
            <option value="down">Down</option>
          </select>
        </div>

        {/* App Type Filter */}
        <div style={styles.filterGroup}>
          <span style={styles.filterLabel}>App Type</span>
          <select
            value={appTypeFilter}
            onChange={(e) => setAppTypeFilter(e.target.value)}
            style={styles.select}
          >
            <option value="all">All</option>
            <option value="arrivals">Arrivals</option>
            <option value="departures">Departures</option>
          </select>
        </div>

        {/* Version Filter */}
        <div style={styles.filterGroup}>
          <span style={styles.filterLabel}>Version</span>
          <select
            value={versionFilter}
            onChange={(e) => setVersionFilter(e.target.value)}
            style={styles.select}
          >
            <option value="all">All</option>
            {availableVersions.map(v => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </div>

        {/* Refresh Button */}
        <div style={{ ...styles.filterGroup, justifyContent: 'flex-end' }}>
          <span style={styles.filterLabel}>&nbsp;</span>
          <button onClick={fetchHealthData} style={styles.button} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={styles.summaryCards}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryNumber}>{summary.total}</div>
          <div style={styles.summaryLabel}>Total Screens</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #4caf50' }}>
          <div style={{ ...styles.summaryNumber, color: '#4caf50' }}>{summary.ok}</div>
          <div style={styles.summaryLabel}>OK</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #ff9800' }}>
          <div style={{ ...styles.summaryNumber, color: '#ff9800' }}>{summary.attention}</div>
          <div style={styles.summaryLabel}>Attention</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #f44336' }}>
          <div style={{ ...styles.summaryNumber, color: '#f44336' }}>{summary.down}</div>
          <div style={styles.summaryLabel}>Down</div>
        </div>
      </div>

      {/* Data Table */}
      {loading ? (
        <div style={styles.loading}>Loading health data...</div>
      ) : devices.length === 0 ? (
        <div style={styles.noData}>
          No devices found for the selected time range and filters.
          <br />
          <span style={{ fontSize: '12px', color: '#666' }}>
            Try adjusting the time range or clearing filters.
          </span>
        </div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Screen Name</th>
              <th style={styles.th}>Version</th>
              <th style={styles.th}>Uptime</th>
              <th style={styles.th}>Render OK</th>
              <th style={styles.th} title="Blank screen classification (v1.2.5+)">Blank Reason</th>
              <th style={styles.th} title="Flights displayed / received from API">Flights</th>
              <th style={styles.th} title="How old is the flight data being displayed">Data Age</th>
              <th style={styles.th} title="Page reloads / App restarts in last hour">Reloads</th>
              <th style={styles.th} title="JavaScript errors in the last 5 minutes">JS Err</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((device, idx) => (
              <tr
                key={device.deviceId}
                onClick={() => handleDeviceClick(device)}
                style={{
                  ...styles.row,
                  backgroundColor: idx % 2 === 0 ? '#000' : '#0a0a0a',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1a1a1a'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#000' : '#0a0a0a'}
              >
                {/* Status */}
                <td style={styles.td}>
                  {renderStatusChip(device.status)}
                  {device.statusReason && (
                    <div style={{ fontSize: '10px', color: '#888', marginTop: '4px' }}>
                      {device.statusReason}
                    </div>
                  )}
                </td>
                {/* Screen Name + App Type + LAN IP (device ID on hover, or shown if no IP yet) */}
                <td style={styles.td}>
                  <div style={{ fontWeight: 'bold' }}>{device.screenName}</div>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '2px' }}>
                    {renderAppTypeChip(device.appType)}
                    <span
                      style={{ ...styles.monospace, fontSize: '10px' }}
                      title={`Device ID: ${device.deviceId}`}
                    >
                      {device.lastIp || device.deviceId}
                    </span>
                  </div>
                </td>
                {/* Version */}
                <td style={styles.td}>
                  <span style={{
                    backgroundColor: '#333',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                  }}>
                    {device.version}
                  </span>
                </td>
                {/* Uptime */}
                <td style={styles.td}>
                  {device.latest?.uptimeMin ?? '-'} min
                </td>
                {/* Render OK */}
                <td style={styles.td}>
                  {device.latest?.renderOk ? (
                    <span style={{ color: '#4caf50', fontWeight: 'bold' }}>Yes</span>
                  ) : (
                    <span style={{ color: '#f44336', fontWeight: 'bold' }}>No</span>
                  )}
                </td>
                {/* Blank Reason (v1.2.5+) */}
                <td style={styles.td}>
                  {device.latest?.blankReason ? (
                    renderBlankReasonChip(device.latest.blankReason)
                  ) : (
                    <span style={{ color: '#666', fontSize: '11px' }}>-</span>
                  )}
                </td>
                {/* Flights (displayed/received) */}
                <td style={styles.td}>
                  {device.latest?.flightsReceived !== undefined ? (
                    renderFlightsRatio(
                      device.latest.flightsReceived,
                      device.latest.flightsDisplayed,
                      device.latest.domDataRowCount
                    )
                  ) : (
                    <span style={{ color: '#666', fontSize: '11px' }}>-</span>
                  )}
                </td>
                {/* Data Age */}
                <td style={styles.td}>
                  <span style={device.latest?.dataAgeSeconds > 180 ? styles.warningValue : {}}>
                    {device.latest?.dataAgeSeconds ?? '-'}s
                  </span>
                </td>
                {/* Reloads (1h) */}
                <td style={styles.td}>
                  <span style={device.latest?.reloadCount1h >= 3 ? styles.warningValue : {}}>
                    {device.latest?.reloadCount1h ?? '-'}
                  </span>
                </td>
                {/* JS Errors (5m) */}
                <td style={styles.td}>
                  <span style={device.latest?.jsErrorCount5m >= 1 ? styles.errorValue : {}}>
                    {device.latest?.jsErrorCount5m ?? '-'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Detail Drawer */}
      <HealthDetailDrawer
        open={drawerOpen}
        device={selectedDevice}
        timeRange={getTimeRange()}
        onClose={handleDrawerClose}
      />
    </div>
  );
}
